import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Send, MapPin, BedDouble, IndianRupee, Loader2, ArrowRight } from 'lucide-react';
import { aiSmartSearch } from '@/lib/api';
import { toast } from 'sonner';

const SAMPLE_PROMPTS = [
  '3BHK family home in Bangalore',
  'Sea-view apartment in Mumbai for rent',
  'Investment plot in Gurgaon, ready to register',
  'Pet-friendly villa near tech parks in Pune',
];

const formatBhk = (p) => {
  const bits = [];
  if (p.bedrooms) bits.push(`${p.bedrooms} BHK`);
  if (p.area) bits.push(`${p.area} ${p.area_unit || 'sqft'}`);
  return bits.join(' · ');
};

const AISmartSearch = () => {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const runSearch = async (q) => {
    const trimmed = (q || '').trim();
    if (trimmed.length < 3) {
      toast.error('Tell us a little more about what you want.');
      return;
    }
    setLoading(true);
    try {
      const data = await aiSmartSearch(trimmed);
      setResult(data);
    } catch (e) {
      toast.error(e.message || 'AI search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="section-gap bg-gradient-to-br from-[#0F172A] via-[#111c33] to-[#0F172A] relative overflow-hidden" data-testid="ai-smart-search-section">
      {/* decorative glow */}
      <div className="pointer-events-none absolute -top-32 -left-32 h-96 w-96 rounded-full bg-[#D4AF37]/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -right-20 h-[28rem] w-[28rem] rounded-full bg-[#D4AF37]/5 blur-3xl" />

      <div className="container-luxury relative">
        <div className="grid lg:grid-cols-[1.05fr_1fr] gap-12 items-start">
          {/* Left - intro & input */}
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/5 text-[#D4AF37] text-xs uppercase tracking-widest mb-6">
              <Sparkles className="h-3.5 w-3.5" /> New · Bharat AI
            </div>
            <h2 className="font-['Plus_Jakarta_Sans'] text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] mb-6">
              Describe your dream home.<br />
              <span className="text-[#D4AF37]">We'll find it for you.</span>
            </h2>
            <p className="text-white/65 max-w-xl leading-relaxed mb-8">
              Skip the filters. Tell our AI concierge what you're looking for in plain English and get three hand-picked recommendations from across India in seconds.
            </p>

            <div className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-2 shadow-2xl">
              <div className="flex flex-col md:flex-row gap-2">
                <textarea
                  rows={2}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      runSearch(query);
                    }
                  }}
                  placeholder="e.g. 3BHK with a balcony in Bangalore, close to good schools and tech parks"
                  className="flex-1 bg-transparent text-white placeholder:text-white/40 resize-none px-4 py-3 outline-none text-[15px]"
                  data-testid="ai-search-input"
                />
                <button
                  onClick={() => runSearch(query)}
                  disabled={loading}
                  className="bg-[#D4AF37] hover:bg-[#c39d2c] disabled:opacity-60 disabled:cursor-not-allowed text-[#0F172A] font-semibold px-6 py-3 rounded-xl flex items-center justify-center gap-2 transition-all hover:scale-[1.02] active:scale-95"
                  data-testid="ai-search-submit"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  {loading ? 'Thinking…' : 'Ask AI'}
                </button>
              </div>
            </div>

            <div className="mt-6">
              <p className="text-white/40 text-xs uppercase tracking-widest mb-3">Try one of these</p>
              <div className="flex flex-wrap gap-2">
                {SAMPLE_PROMPTS.map((p) => (
                  <button
                    key={p}
                    onClick={() => {
                      setQuery(p);
                      runSearch(p);
                    }}
                    className="text-xs sm:text-sm px-3 py-1.5 rounded-full border border-white/15 hover:border-[#D4AF37]/60 hover:bg-[#D4AF37]/10 text-white/75 hover:text-white transition-all"
                    data-testid="ai-sample-prompt"
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Right - results / preview */}
          <div className="min-h-[420px]">
            <AnimatePresence mode="wait">
              {loading && (
                <motion.div
                  key="loading"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="bg-white/5 border border-white/10 rounded-2xl p-8 text-white/70 h-full flex flex-col items-center justify-center"
                >
                  <Loader2 className="h-8 w-8 animate-spin text-[#D4AF37] mb-4" />
                  <p className="text-sm">Bharat AI is reading every active listing for you…</p>
                </motion.div>
              )}

              {!loading && result && (
                <motion.div
                  key="result"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-5"
                  data-testid="ai-search-results"
                >
                  <div className="bg-gradient-to-br from-[#D4AF37]/15 to-transparent border border-[#D4AF37]/30 rounded-2xl p-5">
                    <div className="flex items-start gap-3">
                      <div className="h-9 w-9 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                        <Sparkles className="h-4 w-4 text-[#D4AF37]" />
                      </div>
                      <p className="text-white/85 text-sm leading-relaxed whitespace-pre-line">
                        {result.ai_analysis}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {result.recommendations?.length === 0 && (
                      <div className="bg-white/5 border border-white/10 rounded-2xl p-6 text-white/60 text-sm">
                        We couldn't find a perfect match yet. Try widening your budget or city, or browse all properties below.
                      </div>
                    )}
                    {result.recommendations?.map((p, idx) => (
                      <Link
                        key={p.property_id}
                        to={`/properties/${p.property_id}`}
                        className="group flex gap-4 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#D4AF37]/40 rounded-2xl p-3 transition-all"
                        data-testid={`ai-recommendation-${idx}`}
                      >
                        <img
                          src={p.images?.[0] || 'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=400'}
                          alt={p.title}
                          onError={(e) => { e.currentTarget.src = 'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=400'; }}
                          className="h-24 w-28 rounded-xl object-cover flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0 py-1">
                          <p className="text-white font-medium truncate group-hover:text-[#D4AF37] transition-colors">
                            {p.title}
                          </p>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs text-white/60">
                            <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{p.city}</span>
                            {p.bedrooms ? (
                              <span className="inline-flex items-center gap-1"><BedDouble className="h-3 w-3" />{p.bedrooms} BHK</span>
                            ) : null}
                            {p.area ? (
                              <span className="inline-flex items-center gap-1"><IndianRupee className="h-3 w-3" />{formatBhk(p)}</span>
                            ) : null}
                          </div>
                        </div>
                        <ArrowRight className="h-4 w-4 text-white/40 group-hover:text-[#D4AF37] self-center" />
                      </Link>
                    ))}
                  </div>
                </motion.div>
              )}

              {!loading && !result && (
                <motion.div
                  key="empty"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="bg-white/5 border border-white/10 rounded-2xl p-8 h-full flex flex-col justify-center"
                >
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 rounded-2xl bg-[#D4AF37]/15 flex items-center justify-center flex-shrink-0">
                      <Sparkles className="h-6 w-6 text-[#D4AF37]" />
                    </div>
                    <div>
                      <p className="text-white font-medium mb-2">How it works</p>
                      <ul className="space-y-2 text-white/65 text-sm leading-relaxed list-decimal list-inside marker:text-[#D4AF37]">
                        <li>Type what you want — budget, city, BHK, lifestyle.</li>
                        <li>Bharat AI scans every verified listing.</li>
                        <li>Get 3 personalised matches with a short reason for each.</li>
                      </ul>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AISmartSearch;
